import { useState } from "react"
import { ComposableMap, Geographies, Geography } from "react-simple-maps"
import { cn } from "@/shared/lib/cn"

/**
 * Mapa de cobertura da rede credenciada.
 *
 * Desenha apenas o Nordeste, que e onde a DIMPET opera. A regiao vem da
 * propriedade `regiao` do proprio TopoJSON, e nao de uma lista de UFs escrita a
 * mao: quando a operacao passar de regiao, muda uma constante — nao nove
 * strings espalhadas.
 */

const GEO_URL = "/brasil-estados.json"
const REGION = "Nordeste"

/** Zone ID visual do mapa: centro e escala calibrados para o Nordeste. */
const PROJECTION_CONFIG = { center: [-38.5, -8] as [number, number], scale: 1400 }

const FILL_ACTIVE = "#2E3724"
const FILL_ACTIVE_HOVER = "#405439"
const FILL_INACTIVE = "#DDD9D1"
const FILL_INACTIVE_HOVER = "#CCC8C0"

interface StateProperties {
  sigla: string
  name: string
  regiao: string
}

export interface CoverageMapProps {
  /** UFs com rede credenciada. Sao as unicas clicaveis. */
  activeStates: string[]
  onStateClick: (uf: string) => void
}

export function CoverageMap({ activeStates, onStateClick }: CoverageMapProps) {
  const [hovered, setHovered] = useState<StateProperties | null>(null)

  const isActive = (uf: string) => activeStates.includes(uf)

  return (
    <div className="relative w-full">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={PROJECTION_CONFIG}
        width={400}
        height={420}
        style={{ width: "100%", height: "auto" }}
      >
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies
              .filter((geo) => (geo.properties as StateProperties).regiao === REGION)
              .map((geo) => {
                const properties = geo.properties as StateProperties
                const active = isActive(properties.sigla)

                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onClick={() => active && onStateClick(properties.sigla)}
                    onMouseEnter={() => setHovered(properties)}
                    onMouseLeave={() => setHovered(null)}
                    style={{
                      default: {
                        fill: active ? FILL_ACTIVE : FILL_INACTIVE,
                        stroke: "#fff",
                        strokeWidth: 0.8,
                        outline: "none",
                        cursor: active ? "pointer" : "default",
                        transition: "fill 0.15s",
                      },
                      hover: {
                        fill: active ? FILL_ACTIVE_HOVER : FILL_INACTIVE_HOVER,
                        stroke: "#fff",
                        strokeWidth: 0.8,
                        outline: "none",
                        cursor: active ? "pointer" : "default",
                      },
                      pressed: {
                        fill: active ? FILL_ACTIVE : FILL_INACTIVE,
                        outline: "none",
                      },
                    }}
                  />
                )
              })
          }
        </Geographies>
      </ComposableMap>

      {hovered && (
        <div className="absolute top-2 left-1/2 -translate-x-1/2 pointer-events-none">
          <div
            className={cn(
              "px-3 py-1.5 rounded-lg text-xs font-semibold shadow-lg",
              isActive(hovered.sigla)
                ? "bg-verde-profundo text-white"
                : "bg-white text-verde-profundo/50 border border-verde-profundo/10",
            )}
          >
            {hovered.name} ({hovered.sigla})
            {isActive(hovered.sigla) && (
              <span className="ml-1.5 text-dourado-claro">· Clique para ver unidades</span>
            )}
          </div>
        </div>
      )}

      <div className="flex items-center justify-center gap-5 mt-2">
        <p className="flex items-center gap-1.5 text-[11px] text-verde-profundo/50">
          <span className="w-3 h-3 rounded-sm bg-verde-profundo" aria-hidden="true" />
          Com rede credenciada
        </p>
        <p className="flex items-center gap-1.5 text-[11px] text-verde-profundo/35">
          <span className="w-3 h-3 rounded-sm bg-areia" aria-hidden="true" />
          Em expansão
        </p>
      </div>
    </div>
  )
}
